import BConfig from "../../modules/BConfig";
import BDragDropUploadForm from "../../modules/DragDropUpload/BDragDropUploadForm";
import BSelect from "./BSelect";

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

/**
 * BRecipeForm
 *
 * Manages the recipe create/edit form ([data-form="recipe-version-create"]).
 * All event listeners are delegated to `document` so they work for both
 * server-rendered and JS-added elements without re-binding.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * INGREDIENT LISTS
 * ─────────────────────────────────────────────────────────────────────────────
 * The form can contain one or more ingredient lists ([data-area="inventory"]).
 * Each list groups related ingredients under an optional name (e.g. "Omáčka",
 * "Těsto"). The list header is hidden via CSS when there is only one group.
 *
 * "Přidat seznam surovin" (#add-ingredient-list) clones
 * <template id="recipe-form-ingredient-list"> and appends it.
 *
 * Deleting a whole list uses its own confirmation dialog
 * (deleteIngredientListDialogId). The pending button is stored in
 * pendingIngredientListDeleteBtn and the list is removed only when confirmed
 * ([data-action="confirm-delete-ingredient-list"]). Enter also confirms;
 * Zrušit / X closes without deleting.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * INGREDIENT ROWS
 * ─────────────────────────────────────────────────────────────────────────────
 * Each ingredient row is [data-area="inventory-item-option-ingredient"] and
 * comes in three variants:
 *
 *   main-item     – a primary ingredient, searched via the autocomplete input
 *                   (.b-recipe-form__ingredient-search). Selecting a result
 *                   clones <template id="recipe-form-ingredient-main">, fills
 *                   in the name, thumbnail, and practical unit options, then
 *                   appends it to .b-recipe-form__inventory-items.
 *
 *   alt-item      – an alternative ingredient for the same item slot (e.g.
 *                   "nebo máslo"). Added from the actions dropdown (▾) via
 *                   "Přidat alternativní suroviny". Clones
 *                   <template id="recipe-form-ingredient-alt">.
 *
 *   specific-item – a specific product (deli/shop item) linked to an
 *                   ingredient. Added via "Přidat konkrétní produkt" in the
 *                   actions dropdown. Clones
 *                   <template id="recipe-form-ingredient-specific">.
 *
 * The actions dropdown (.b-recipe-form__actions-dropdown) is dynamically
 * injected next to the active ingredient when clicking its .drop-trig button,
 * and removed when clicking away or selecting an action.
 *
 * An optional free-text note can be added to any ingredient row via
 * "Přidat poznámku" in the dropdown; it renders a resizable textarea
 * (.b-recipe-form__ingredient-note).
 *
 * Deleting a main-item also removes all its alt-items and specific-items that
 * follow it (they are siblings until the next main-item).
 *
 * Deleting any ingredient row (.delete button) opens a dedicated confirmation
 * dialog (deleteIngredientDialogId). The pending button is stored in
 * pendingIngredientDeleteBtn and the row is removed only when confirmed
 * ([data-action="confirm-delete-ingredient"]). Enter also confirms.
 *
 * .b-recipe-form__inventory-items--empty is toggled to hide the items container
 * when it has no children (handled by updateInventoryItemsVisibility()).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * INGREDIENT AUTOCOMPLETE
 * ─────────────────────────────────────────────────────────────────────────────
 * Typing in .b-recipe-form__ingredient-search fires a debounced fetch to
 * api.v2.ingredientNames.searchSolr?query=…. Results appear in the adjacent
 * .b-dropdown. Keyboard navigation (↑ ↓ Enter Escape) and click selection are
 * supported. Pressing Enter with no result highlighted adds the typed name as
 * a free-text ingredient (no DB id).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PREPARATION STEPS
 * ─────────────────────────────────────────────────────────────────────────────
 * Steps live in .b-recipe-form__steps-list. Each step is
 * [data-area="step"] and is rendered from
 * <template id="recipe-form-step"> / prototype.step.twig.
 *
 * On initial page load, saved steps are server-rendered. If there are none, a
 * single empty step is rendered server-side so the user always sees at least
 * one step form.
 *
 * "Přidat krok" (#step-place) clones the template, appends it to the list,
 * calls refreshInputNames(), and focuses the description textarea of the
 * new step.
 *
 * Each step contains:
 *   - description textarea        [data-purpose="description"]
 *   - photo upload                (.drag-drop-upload, managed by BDragDropUploadForm)
 *   - recipe search + recipe list (.b-recipe-form__step-recipes)
 *   - video URL input             [data-purpose="videoUrl"]
 *   - remove button               [data-action="remove-step"]
 *
 * Clicking a remove button opens a confirmation <dialog> (deleteStepDialogId).
 * The pending button is stored in pendingStepDeleteBtn and the step is removed
 * only when confirmed ([data-action="confirm-delete-step"]). Enter also
 * confirms; Zrušit / X closes without deleting.
 *
 * The remove button is kept in the layout at all times (no display:none) but
 * is set to visibility:hidden when there is only one step, preventing an
 * accidental empty form. This is managed by updateStepRemoveVisibility().
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * CONFIRMATION DIALOGS
 * ─────────────────────────────────────────────────────────────────────────────
 * Four independent <dialog> elements: three for destructive actions (delete
 * step, delete ingredient, delete ingredient list) and one for abandoning the
 * form (cancel). Each has its own static title, body text, and JS state:
 *
 *   deleteStepDialogId              "recipe-form-delete-step-dialog"
 *     state:  pendingStepDeleteBtn
 *     confirm: [data-action="confirm-delete-step"]
 *     cancel:  [data-action="cancel-delete-step"]
 *
 *   deleteIngredientDialogId        "recipe-form-delete-ingredient-dialog"
 *     state:  pendingIngredientDeleteBtn
 *     confirm: [data-action="confirm-delete-ingredient"]
 *     cancel:  [data-action="cancel-delete-ingredient"]
 *
 *   deleteIngredientListDialogId    "recipe-form-delete-ingredient-list-dialog"
 *     state:  pendingIngredientListDeleteBtn
 *     confirm: [data-action="confirm-delete-ingredient-list"]
 *     cancel:  [data-action="cancel-delete-ingredient-list"]
 *
 *   cancelDialogId                  "recipe-form-cancel-dialog"
 *     state:  pendingCancelUrl (from the Zrušit link href)
 *     trigger: [data-action="recipe-form-cancel"] (the "Zrušit" link)
 *     confirm: [data-action="recipe-form-cancel-confirm"] → navigate to pendingCancelUrl
 *     cancel:  [data-action="recipe-form-cancel-dismiss"] or header X / Escape
 *
 * All four dialogs share the same UX pattern: clicking the confirm button or
 * (for delete dialogs) pressing Enter executes the pending action and closes
 * the dialog; clicking Zrušit, the header X, or pressing Escape closes without
 * acting. The cancel dialog does not respond to Enter (user must click).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * STEP RECIPE SEARCH (LINKED RECIPES)
 * ─────────────────────────────────────────────────────────────────────────────
 * Each step can have zero or more linked recipes. The search input
 * (.b-recipe-form__recipe-search) fires a debounced fetch to
 * api.v2.recipes.search?query=…. Selecting a result from the dropdown builds
 * a pill (.b-recipe-form__step-recipe) with thumbnail, title link, and a
 * remove button ([data-action="remove-step-recipe"]).
 *
 * The recipes list (.b-recipe-form__step-recipes-list) is hidden via the
 * `hidden` attribute when empty (updateStepRecipesVisibility()).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * INPUT NAMING (refreshInputNames)
 * ─────────────────────────────────────────────────────────────────────────────
 * Because elements are added/removed/reordered dynamically, all form field
 * names and IDs are assigned (or re-assigned) by refreshInputNames() rather
 * than being hardcoded in templates. It is called after every structural DOM
 * change.
 *
 * Ingredient names follow:
 *   inventories[n][items][m][options][o][ingredients][1][field]
 *   inventories[n][items][m][options][o][products][p][field]
 *
 * Step names follow:
 *   steps[n][description]
 *   steps[n][storageFileIds][]
 *   steps[n][videoUrl]
 *   steps[n][recipeIds][]
 *
 * Labels in step forms receive matching `for` attributes so clicking a label
 * focuses the correct input (setInputIdAndLabel()).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FORM SUBMISSION AND CANCEL
 * ─────────────────────────────────────────────────────────────────────────────
 * Two submit buttons control privacy: "Uložit" (private) and "Uložit a
 * publikovat" (public). Clicking either sets the value of the hidden
 * input[name="privacy"] before submitting, so the server receives the correct
 * target privacy level.
 *
 * The "Zrušit" link ([data-action="recipe-form-cancel"]) does not navigate
 * immediately; it opens the cancel confirmation dialog. "Pokračovat v editaci"
 * closes the dialog; "Odejít" navigates to the link href (returnUrl or
 * account recipes).
 */
export default class BRecipeForm
{
	static selector = () => `[data-form="recipe-version-create"]`;
	static activeIngredient = null;
	static activeDropdown = null;
	static searchTimers = new WeakMap();
	static highlightedIndex = -1;
	static pendingStepDeleteBtn = null;
	static deleteStepDialogId = "recipe-form-delete-step-dialog";
	static pendingIngredientDeleteBtn = null;
	static deleteIngredientDialogId = "recipe-form-delete-ingredient-dialog";
	static pendingIngredientListDeleteBtn = null;
	static deleteIngredientListDialogId = "recipe-form-delete-ingredient-list-dialog";
	static cancelDialogId = "recipe-form-cancel-dialog";
	static pendingCancelUrl = null;

	static showModalSafe(dialog)
	{
		const scrollX = window.scrollX;
		const scrollY = window.scrollY;
		dialog.showModal();
		window.scrollTo(scrollX, scrollY);
		requestAnimationFrame(() => window.scrollTo(scrollX, scrollY));
	}

	static initialize()
	{
		if (!this.getForm()) return;

		this.hideActionsTemplate();
		this.addEventListeners();
		this.refreshInputNames();
		this.updateInventoryItemsVisibility();
		this.updateStepRecipesVisibility();
		this.updateStepRemoveVisibility();
	}

	static getForm()
	{
		return document.querySelector(this.selector());
	}

	static hideActionsTemplate()
	{
		const menu = document.getElementById("drop-actions");
		if (menu) menu.hidden = true;
	}

	static addEventListeners()
	{
		document.addEventListener("click", event => {
			const button = event.target.closest(`button[type="submit"][data-privacy]`);
			if (!button) return;

			const form = button.closest(this.selector());
			if (!form) return;

			event.preventDefault();
			const privacyInput = form.querySelector(`input[name="privacy"]`);
			if (privacyInput) {
				privacyInput.value = button.dataset.privacy;
			}
			form.submit();
		});

		document.addEventListener("click", event => {
			const trig = event.target.closest(".drop-trig");
			if (trig) {
				event.preventDefault();
				event.stopPropagation();
				this.toggleActionsDropdown(trig);
				return;
			}

			if (this.activeDropdown && !event.target.closest(".b-recipe-form__actions-dropdown")) {
				this.closeActionsDropdown();
			}
		});

		document.addEventListener("click", event => {
			if (!event.target.closest(".b-recipe-form__actions-dropdown .add-note")) return;
			event.preventDefault();
			this.handleAddNote();
		});

		document.addEventListener("click", event => {
			if (!event.target.closest(".b-recipe-form__actions-dropdown .add-alt")) return;
			event.preventDefault();
			this.handleAddAlt();
		});

		document.addEventListener("click", event => {
			if (!event.target.closest(".b-recipe-form__actions-dropdown .add-prod")) return;
			event.preventDefault();
			this.handleAddProd();
		});

		document.addEventListener("click", event => {
			const deleteBtn = event.target.closest(`[data-area="inventory-item-option-ingredient"] .delete`);
			if (!deleteBtn) return;
			event.preventDefault();
			this.openDeleteIngredientDialog(deleteBtn);
		});

		document.addEventListener("click", event => {
			const link = event.target.closest(`[data-action="recipe-form-cancel"]`);
			if (!link || link.getAttribute("href") === "javascript:void(0)") return;
			event.preventDefault();
			this.openCancelDialog(link);
		});

		document.addEventListener("click", event => {
			const btn = event.target.closest(`[data-area="step"] [data-action="remove-step"]`);
			if (!btn) return;
			event.preventDefault();
			this.openDeleteStepDialog(btn);
		});

		document.addEventListener("click", event => {
			if (!event.target.closest(`[data-action="confirm-delete-step"]`)) return;
			event.preventDefault();
			if (this.pendingStepDeleteBtn) {
				this.handleDeleteStep(this.pendingStepDeleteBtn);
				this.pendingStepDeleteBtn = null;
			}
			const dialog = document.getElementById(this.deleteStepDialogId);
			if (dialog) dialog.close();
		});

		document.addEventListener("click", event => {
			if (!event.target.closest(`[data-action="cancel-delete-step"]`)) return;
			event.preventDefault();
			const dialog = document.getElementById(this.deleteStepDialogId);
			if (dialog) dialog.close();
		});

		document.addEventListener("keydown", event => {
			if (event.key !== "Enter") return;
			const dialog = document.getElementById(this.deleteStepDialogId);
			if (!dialog || !dialog.open) return;
			event.preventDefault();
			if (this.pendingStepDeleteBtn) {
				this.handleDeleteStep(this.pendingStepDeleteBtn);
			}
			dialog.close();
		});

		document.addEventListener("click", event => {
			if (!event.target.closest(`[data-action="confirm-delete-ingredient"]`)) return;
			event.preventDefault();
			if (this.pendingIngredientDeleteBtn) {
				this.handleDeleteIngredient(this.pendingIngredientDeleteBtn);
				this.pendingIngredientDeleteBtn = null;
			}
			const dialog = document.getElementById(this.deleteIngredientDialogId);
			if (dialog) dialog.close();
		});

		document.addEventListener("click", event => {
			if (!event.target.closest(`[data-action="cancel-delete-ingredient"]`)) return;
			event.preventDefault();
			const dialog = document.getElementById(this.deleteIngredientDialogId);
			if (dialog) dialog.close();
		});

		document.addEventListener("keydown", event => {
			if (event.key !== "Enter") return;
			const dialog = document.getElementById(this.deleteIngredientDialogId);
			if (!dialog || !dialog.open) return;
			event.preventDefault();
			if (this.pendingIngredientDeleteBtn) {
				this.handleDeleteIngredient(this.pendingIngredientDeleteBtn);
				this.pendingIngredientDeleteBtn = null;
			}
			dialog.close();
		});

		document.addEventListener("click", event => {
			if (!event.target.closest(`[data-action="confirm-delete-ingredient-list"]`)) return;
			event.preventDefault();
			if (this.pendingIngredientListDeleteBtn) {
				this.handleDeleteIngredientList(this.pendingIngredientListDeleteBtn);
				this.pendingIngredientListDeleteBtn = null;
			}
			const dialog = document.getElementById(this.deleteIngredientListDialogId);
			if (dialog) dialog.close();
		});

		document.addEventListener("click", event => {
			if (!event.target.closest(`[data-action="cancel-delete-ingredient-list"]`)) return;
			event.preventDefault();
			const dialog = document.getElementById(this.deleteIngredientListDialogId);
			if (dialog) dialog.close();
		});

		document.addEventListener("click", event => {
			if (!event.target.closest(`[data-action="recipe-form-cancel-confirm"]`)) return;
			event.preventDefault();
			if (this.pendingCancelUrl) {
				window.location.href = this.pendingCancelUrl;
				this.pendingCancelUrl = null;
			}
			const dialog = document.getElementById(this.cancelDialogId);
			if (dialog) dialog.close();
		});

		document.addEventListener("click", event => {
			if (!event.target.closest(`[data-action="recipe-form-cancel-dismiss"]`)) return;
			event.preventDefault();
			this.pendingCancelUrl = null;
			const dialog = document.getElementById(this.cancelDialogId);
			if (dialog) dialog.close();
		});

		document.addEventListener("keydown", event => {
			if (event.key !== "Enter") return;
			const dialog = document.getElementById(this.deleteIngredientListDialogId);
			if (!dialog || !dialog.open) return;
			event.preventDefault();
			if (this.pendingIngredientListDeleteBtn) {
				this.handleDeleteIngredientList(this.pendingIngredientListDeleteBtn);
				this.pendingIngredientListDeleteBtn = null;
			}
			dialog.close();
		});

		document.addEventListener("click", event => {
			if (!event.target.closest("#step-place")) return;
			event.preventDefault();
			this.handleAddStep();
		});

		document.addEventListener("click", event => {
			if (!event.target.closest("#add-ingredient-list")) return;
			event.preventDefault();
			this.handleAddIngredientList();
		});

		document.addEventListener("click", event => {
			const btn = event.target.closest(".delete-ingredient-list");
			if (!btn) return;
			event.preventDefault();
			this.openDeleteIngredientListDialog(btn);
		});

		document.addEventListener("click", event => {
			const btn = event.target.closest(`[data-action="remove-step-recipe"]`);
			if (!btn) return;
			event.preventDefault();
			btn.closest(".b-recipe-form__step-recipe")?.remove();
			this.refreshInputNames();
			this.updateStepRecipesVisibility();
		});

		document.addEventListener("keydown", event => {
			if (event.key === "Escape") {
				this.closeActionsDropdown();
				this.removeActiveSearchInput();
				this.closeAllDropdowns();
			}
		});

		// --- Ingredient Autocomplete ---

		document.addEventListener("input", event => {
			const input = event.target.closest(".b-recipe-form__ingredient-search");
			if (!input) return;
			this.handleIngredientSearchInput(input);
		});

		document.addEventListener("keydown", event => {
			const input = event.target.closest(".b-recipe-form__ingredient-search");
			if (!input) return;
			this.handleIngredientSearchKeydown(event, input);
		});

		document.addEventListener("click", event => {
			const resultItem = event.target.closest(".b-recipe-form__ingredient-search-wrap .b-dropdown__option");
			if (!resultItem) return;
			event.preventDefault();
			this.selectIngredientResult(resultItem);
		});

		document.addEventListener("click", event => {
			if (!event.target.closest(".b-recipe-form__ingredient-search-wrap")) {
				this.closeAllIngredientResults();
			}
		});

		document.addEventListener("focusin", event => {
			const input = event.target.closest(".b-recipe-form__ingredient-search");
			if (!input) return;
			const wrap = input.closest(".b-recipe-form__ingredient-search-wrap");
			const results = wrap?.querySelector(".b-dropdown");
			if (results?.children.length > 0) {
				this.resetIngredientSearchDropdownScroll(results);
				results.hidden = false;
			}
		});

		// --- Recipe Autocomplete ---

		document.addEventListener("input", event => {
			const input = event.target.closest(".b-recipe-form__recipe-search");
			if (!input) return;
			this.handleRecipeSearchInput(input);
		});

		document.addEventListener("keydown", event => {
			const input = event.target.closest(".b-recipe-form__recipe-search");
			if (!input) return;
			this.handleRecipeSearchKeydown(event, input);
		});

		document.addEventListener("click", event => {
			const resultItem = event.target.closest(".b-recipe-form__recipe-search-wrap .b-dropdown__option");
			if (!resultItem) return;
			event.preventDefault();
			this.selectRecipeResult(resultItem);
		});

		document.addEventListener("click", event => {
			if (!event.target.closest(".b-recipe-form__recipe-search-wrap")) {
				this.closeAllRecipeResults();
			}
		});

		document.addEventListener("focusin", event => {
			const input = event.target.closest(".b-recipe-form__recipe-search");
			if (!input) return;
			const wrap = input.closest(".b-recipe-form__recipe-search-wrap");
			const results = wrap?.querySelector(".b-dropdown");
			if (results?.children.length > 0) {
				results.hidden = false;
			}
		});
	}

	// --- Ingredient Autocomplete ---

	static resetIngredientSearchDropdownScroll(results)
	{
		if (!results) return;
		results.scrollTop = 0;
	}

	static handleIngredientSearchInput(input)
	{
		const query = input.value.trim();
		const wrap = input.closest(".b-recipe-form__ingredient-search-wrap");
		const results = wrap?.querySelector(".b-dropdown");

		if (query.length < MIN_QUERY_LENGTH) {
			if (results) {
				results.innerHTML = "";
				results.hidden = true;
				this.resetIngredientSearchDropdownScroll(results);
			}
			return;
		}

		let timer = this.searchTimers.get(input);
		if (timer) clearTimeout(timer);

		this.searchTimers.set(input, setTimeout(() => {
			this.fetchIngredientNames(query, input);
		}, DEBOUNCE_MS));
	}

	static handleIngredientSearchKeydown(event, input)
	{
		const wrap = input.closest(".b-recipe-form__ingredient-search-wrap");
		const results = wrap?.querySelector(".b-dropdown");
		if (!results) return;

		const items = results.querySelectorAll(".b-dropdown__option");

		if (event.key === "ArrowDown") {
			event.preventDefault();
			this.highlightedIndex = Math.min(this.highlightedIndex + 1, items.length - 1);
			this.updateHighlight(items);
		} else if (event.key === "ArrowUp") {
			event.preventDefault();
			this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
			this.updateHighlight(items);
		} else if (event.key === "Enter") {
			event.preventDefault();
			if (this.highlightedIndex >= 0 && items[this.highlightedIndex]) {
				this.selectIngredientResult(items[this.highlightedIndex]);
			} else if (input.value.trim()) {
				this.addMainIngredientByName(input, input.value.trim());
			}
		} else if (event.key === "Escape") {
			results.innerHTML = "";
			results.hidden = true;
			this.resetIngredientSearchDropdownScroll(results);
			this.highlightedIndex = -1;
		}
	}

	static updateHighlight(items)
	{
		items.forEach((item, i) => {
			item.classList.toggle("b-dropdown__option--active", i === this.highlightedIndex);
		});
	}

	static async fetchIngredientNames(query, input)
	{
		const wrap = input.closest(".b-recipe-form__ingredient-search-wrap");
		const results = wrap?.querySelector(".b-dropdown");
		if (!results) return;

		const baseUrl = BConfig.getRoute("api.v2.ingredientNames.searchSolr");
		if (!baseUrl) return;

		const url = `${baseUrl}?query=${encodeURIComponent(query)}`;

		try {
			const response = await fetch(url, { headers: { "Accept": "application/json" } });
			const data = await response.json();
			const items = Array.isArray(data) ? data : (data.items || []);

			this.highlightedIndex = -1;
			results.innerHTML = "";

			if (items.length === 0) {
				results.innerHTML = `<div class="b-dropdown__empty">Nic nenalezeno</div>`;
				this.resetIngredientSearchDropdownScroll(results);
				results.hidden = false;
				return;
			}

			items.forEach(item => {
				const el = document.createElement("div");
				el.className = "b-dropdown__option";
				el.dataset.ingredientData = JSON.stringify(item);

				const thumb = item.ingredient?.thumbnail;
				if (thumb) {
					const img = document.createElement("img");
					img.className = "b-dropdown__option-img";
					img.src = thumb;
					img.alt = "";
					img.loading = "lazy";
					el.appendChild(img);
				}

				const label = document.createElement("span");
				label.className = "b-dropdown__option-label";
				label.textContent = item.title || item.name || "";
				el.appendChild(label);

				results.appendChild(el);
			});

			this.resetIngredientSearchDropdownScroll(results);
			results.hidden = false;
		} catch (err) {
			console.error("Ingredient search failed:", err);
		}
	}

	static selectIngredientResult(resultEl)
	{
		let data;
		try {
			data = JSON.parse(resultEl.dataset.ingredientData);
		} catch { return; }

		const wrap = resultEl.closest(".b-recipe-form__ingredient-search-wrap");
		const input = wrap?.querySelector(".b-recipe-form__ingredient-search");
		const results = wrap?.querySelector(".b-dropdown");
		const inventory = wrap?.closest(".b-recipe-form__ingredient-list-contents");
		const itemList = inventory?.querySelector(".b-recipe-form__inventory-items");

		if (!itemList) return;

		const template = document.getElementById("recipe-form-ingredient-main");
		if (!template) return;

		const clone = template.content.cloneNode(true);
		const cloneEl = clone.firstElementChild;

		const header = clone.querySelector(".head");
		if (header) header.textContent = data.title || data.name || "";

		const nameIdInput = clone.querySelector(`[data-purpose="ingredient-name-id"]`);
		if (nameIdInput) nameIdInput.value = data.id || "";

		const nameInput = clone.querySelector(`[data-purpose="ingredient-name"]`);
		if (nameInput) nameInput.value = data.title || data.name || "";

		const thumbWrap = clone.querySelector(`[data-purpose="ingredient-thumb-wrap"]`);
		if (thumbWrap) {
			const thumb = thumbWrap.querySelector(".b-recipe-form__ingredient-thumb");
			if (data.ingredient?.thumbnail) {
				if (thumb) {
					thumb.src = data.ingredient.thumbnail;
				} else {
					const img = document.createElement("img");
					img.className = "b-recipe-form__ingredient-thumb";
					img.src = data.ingredient.thumbnail;
					img.alt = "";
					thumbWrap.appendChild(img);
				}
				thumbWrap.style.display = "";
			} else {
				thumbWrap.style.display = "none";
			}
		}

		if (data.ingredient?.practicalUnits?.length) {
			const unitSelect = clone.querySelector(`[data-purpose="practical-unit-id"]`);
			if (unitSelect) {
				unitSelect.innerHTML = "";
				data.ingredient.practicalUnits.forEach(pu => {
					const opt = document.createElement("option");
					opt.value = pu.id;
					opt.textContent = pu.title;
					if (pu.isDefault) opt.selected = true;
					unitSelect.appendChild(opt);
				});
			}
		}

		itemList.appendChild(clone);
		if (cloneEl) this.initUnitSelects(cloneEl);
		this.refreshInputNames();
		this.updateInventoryItemsVisibility();

		if (input) input.value = "";
		if (results) {
			results.innerHTML = "";
			results.hidden = true;
			this.resetIngredientSearchDropdownScroll(results);
		}
		this.highlightedIndex = -1;

		setTimeout(() => {
			const amountInput = cloneEl?.querySelector(`[data-purpose="net-amount"]`);
			if (amountInput) amountInput.focus();
		}, 50);
	}

	static addMainIngredientByName(input, name)
	{
		const wrap = input.closest(".b-recipe-form__ingredient-search-wrap");
		const results = wrap?.querySelector(".b-dropdown");
		const inventory = wrap?.closest(".b-recipe-form__ingredient-list-contents");
		const itemList = inventory?.querySelector(".b-recipe-form__inventory-items");

		if (!itemList) return;

		const template = document.getElementById("recipe-form-ingredient-main");
		if (!template) return;

		const clone = template.content.cloneNode(true);
		const cloneEl = clone.firstElementChild;

		const header = clone.querySelector(".head");
		if (header) header.textContent = name;

		const nameInput = clone.querySelector(`[data-purpose="ingredient-name"]`);
		if (nameInput) nameInput.value = name;

		itemList.appendChild(clone);
		if (cloneEl) this.initUnitSelects(cloneEl);
		this.refreshInputNames();
		this.updateInventoryItemsVisibility();

		input.value = "";
		if (results) {
			results.innerHTML = "";
			results.hidden = true;
			this.resetIngredientSearchDropdownScroll(results);
		}

		setTimeout(() => {
			const amountInput = cloneEl?.querySelector(`[data-purpose="net-amount"]`);
			if (amountInput) amountInput.focus();
		}, 50);
	}

	static closeAllIngredientResults()
	{
		document.querySelectorAll(".b-recipe-form__ingredient-search-wrap .b-dropdown").forEach(el => {
			el.hidden = true;
			this.resetIngredientSearchDropdownScroll(el);
		});
		this.highlightedIndex = -1;
	}

	// --- Recipe Autocomplete ---

	static recipeHighlightedIndex = -1;

	static handleRecipeSearchInput(input)
	{
		const query = input.value.trim();
		const wrap = input.closest(".b-recipe-form__recipe-search-wrap");
		const results = wrap?.querySelector(".b-dropdown");

		if (query.length < MIN_QUERY_LENGTH) {
			if (results) {
				results.innerHTML = "";
				results.hidden = true;
			}
			return;
		}

		let timer = this.searchTimers.get(input);
		if (timer) clearTimeout(timer);

		this.searchTimers.set(input, setTimeout(() => {
			this.fetchRecipes(query, input);
		}, DEBOUNCE_MS));
	}

	static handleRecipeSearchKeydown(event, input)
	{
		const wrap = input.closest(".b-recipe-form__recipe-search-wrap");
		const results = wrap?.querySelector(".b-dropdown");
		if (!results) return;

		const items = results.querySelectorAll(".b-dropdown__option");

		if (event.key === "ArrowDown") {
			event.preventDefault();
			this.recipeHighlightedIndex = Math.min(this.recipeHighlightedIndex + 1, items.length - 1);
			this.updateRecipeHighlight(items);
		} else if (event.key === "ArrowUp") {
			event.preventDefault();
			this.recipeHighlightedIndex = Math.max(this.recipeHighlightedIndex - 1, 0);
			this.updateRecipeHighlight(items);
		} else if (event.key === "Enter") {
			event.preventDefault();
			if (this.recipeHighlightedIndex >= 0 && items[this.recipeHighlightedIndex]) {
				this.selectRecipeResult(items[this.recipeHighlightedIndex]);
			}
		} else if (event.key === "Escape") {
			results.innerHTML = "";
			results.hidden = true;
			this.recipeHighlightedIndex = -1;
		}
	}

	static updateRecipeHighlight(items)
	{
		items.forEach((item, i) => {
			item.classList.toggle("b-dropdown__option--active", i === this.recipeHighlightedIndex);
		});
	}

	static async fetchRecipes(query, input)
	{
		const wrap = input.closest(".b-recipe-form__recipe-search-wrap");
		const results = wrap?.querySelector(".b-dropdown");
		if (!results) return;

		const baseUrl = BConfig.getRoute("api.v2.recipes.search");
		if (!baseUrl) return;

		const url = `${baseUrl}?query=${encodeURIComponent(query)}`;

		try {
			const response = await fetch(url, { headers: { "Accept": "application/json" } });
			const data = await response.json();
			const items = Array.isArray(data) ? data : (data.items || []);

			this.recipeHighlightedIndex = -1;
			results.innerHTML = "";

			if (items.length === 0) {
				results.innerHTML = `<div class="b-dropdown__empty">Nic nenalezeno</div>`;
				results.hidden = false;
				return;
			}

			items.forEach(item => {
				const el = document.createElement("div");
				el.className = "b-dropdown__option";
				el.dataset.recipeData = JSON.stringify(item);

				const thumb = item.imageUrl;
				if (thumb) {
					const img = document.createElement("img");
					img.className = "b-dropdown__option-img";
					img.src = thumb;
					img.alt = "";
					img.loading = "lazy";
					el.appendChild(img);
				}

				const label = document.createElement("span");
				label.className = "b-dropdown__option-label";
				label.textContent = item.title || item.name || "";
				el.appendChild(label);

				results.appendChild(el);
			});

			results.hidden = false;
		} catch (err) {
			console.error("Recipe search failed:", err);
		}
	}

	static selectRecipeResult(resultEl)
	{
		let data;
		try {
			data = JSON.parse(resultEl.dataset.recipeData);
		} catch { return; }

		const wrap = resultEl.closest(".b-recipe-form__recipe-search-wrap");
		const input = wrap?.querySelector(".b-recipe-form__recipe-search");
		const results = wrap?.querySelector(".b-dropdown");
		const recipesList = wrap?.closest(".b-recipe-form__step-recipes")?.querySelector(".b-recipe-form__step-recipes-list");

		if (!recipesList) return;

		const pill = document.createElement("div");
		pill.className = "b-recipe-form__step-recipe";

		const row = document.createElement("div");
		row.className = "b-recipe-form__step-recipe-row";

		const thumb = document.createElement("div");
		thumb.className = "b-recipe-form__step-recipe-thumb";
		thumb.dataset.purpose = "recipe-thumb-wrap";
		if (data.imageUrl) {
			const img = document.createElement("img");
			img.src = data.imageUrl;
			img.alt = "";
			thumb.appendChild(img);
		} else {
			thumb.hidden = true;
		}
		row.appendChild(thumb);

		const name = document.createElement("div");
		name.className = "b-recipe-form__step-recipe-name";
		const link = document.createElement("a");
		link.target = "_blank";
		link.textContent = data.title || data.name || "";
		if (data.urls && data.urls["recipes.view"]) {
			link.href = data.urls["recipes.view"];
		}
		name.appendChild(link);
		row.appendChild(name);

		const removeBtn = document.createElement("button");
		removeBtn.type = "button";
		removeBtn.className = "b-recipe-form__remove-btn";
		removeBtn.dataset.action = "remove-step-recipe";
		removeBtn.title = "Odstranit";
		removeBtn.innerHTML = "<span class=\"material-icons-outlined\">delete</span>";
		row.appendChild(removeBtn);

		pill.appendChild(row);

		const hidden = document.createElement("input");
		hidden.type = "hidden";
		hidden.dataset.purpose = "recipe-id";
		hidden.value = data.id || "";
		pill.appendChild(hidden);

		recipesList.appendChild(pill);
		recipesList.hidden = false;

		this.refreshInputNames();

		if (input) input.value = "";
		if (results) {
			results.innerHTML = "";
			results.hidden = true;
		}
		this.recipeHighlightedIndex = -1;
	}

	static closeAllRecipeResults()
	{
		document.querySelectorAll(".b-recipe-form__recipe-search-wrap .b-dropdown").forEach(el => {
			el.hidden = true;
		});
		this.recipeHighlightedIndex = -1;
	}

	static closeAllDropdowns()
	{
		this.closeAllIngredientResults();
		this.closeAllRecipeResults();
	}

	// --- Actions Dropdown ---

	static toggleActionsDropdown(trig)
	{
		const ingredient = trig.closest(`[data-area="inventory-item-option-ingredient"]`);
		if (!ingredient) return;

		if (this.activeDropdown && this.activeIngredient === ingredient) {
			this.closeActionsDropdown();
			return;
		}

		this.closeActionsDropdown();
		this.activeIngredient = ingredient;

		const template = document.getElementById("drop-actions");
		if (!template) return;

		const dropdown = document.createElement("div");
		dropdown.className = "b-recipe-form__actions-dropdown";
		dropdown.innerHTML = template.innerHTML;

		ingredient.appendChild(dropdown);
		this.activeDropdown = dropdown;
	}

	static closeActionsDropdown()
	{
		if (this.activeDropdown) {
			this.activeDropdown.remove();
			this.activeDropdown = null;
		}
	}

	// --- Add Note ---

	static handleAddNote()
	{
		const ingredient = this.activeIngredient;
		this.closeActionsDropdown();
		if (!ingredient) return;

		const noteWrap = ingredient.querySelector(".note-wrap");
		if (!noteWrap) return;

		if (noteWrap.querySelector(".note, .b-recipe-form__ingredient-note")) return;

		const noteDiv = document.createElement("div");
		noteDiv.className = "b-input b-recipe-form__ingredient-note note";

		const textarea = document.createElement("textarea");
		textarea.className = "b-input__input";
		textarea.placeholder = "např. najemno";
		textarea.dataset.purpose = "comment";
		noteDiv.appendChild(textarea);
		noteWrap.appendChild(noteDiv);

		textarea.focus();
		this.refreshInputNames();
	}

	// --- Add Alternative Ingredient ---

	static handleAddAlt()
	{
		const ingredient = this.activeIngredient;
		this.closeActionsDropdown();
		if (!ingredient) return;

		this.showSearchInput(ingredient, "alt");
	}

	// --- Add Specific Product ---

	static handleAddProd()
	{
		const ingredient = this.activeIngredient;
		this.closeActionsDropdown();
		if (!ingredient) return;

		this.showSearchInput(ingredient, "specific");
	}

	static showSearchInput(afterElement, type)
	{
		this.removeActiveSearchInput();

		const wrapper = document.createElement("div");
		wrapper.className = "b-recipe-form__search-input-wrapper";
		wrapper.dataset.searchType = type;

		const inputDiv = document.createElement("div");
		inputDiv.className = "b-input";

		const input = document.createElement("input");
		input.type = "text";
		input.className = "b-input__input";
		input.placeholder = type === "alt"
			? "Zadejte název alternativní suroviny…"
			: "Zadejte název produktu…";

		inputDiv.appendChild(input);
		wrapper.appendChild(inputDiv);
		afterElement.after(wrapper);
		input.focus();

		const submit = () => {
			const name = input.value.trim();
			if (name) {
				this.createIngredientRow(type, name, afterElement);
			}
			wrapper.remove();
		};

		input.addEventListener("keydown", event => {
			if (event.key === "Enter") {
				event.preventDefault();
				submit();
			}
			if (event.key === "Escape") {
				wrapper.remove();
			}
		});

		let blurHandled = false;
		input.addEventListener("blur", () => {
			if (blurHandled) return;
			blurHandled = true;
			setTimeout(() => submit(), 100);
		});
	}

	static removeActiveSearchInput()
	{
		document.querySelectorAll(".b-recipe-form__search-input-wrapper").forEach(el => el.remove());
	}

	static createIngredientRow(type, name, afterElement)
	{
		const template = document.getElementById(`recipe-form-ingredient-${type}`);
		if (!template) return;

		const clone = template.content.cloneNode(true);

		if (type === "alt") {
			const nameInput = clone.querySelector(`[data-purpose="ingredient-name"]`);
			if (nameInput) nameInput.value = name;
			const valSpan = clone.querySelector(".val");
			if (valSpan) valSpan.textContent = name;
		} else if (type === "specific") {
			const valSpan = clone.querySelector(".val");
			if (valSpan) valSpan.textContent = name;
		}

		afterElement.after(clone);
		this.refreshInputNames();
	}

	// --- Delete Ingredient ---

	static handleDeleteIngredient(deleteBtn)
	{
		const item = deleteBtn.closest(`[data-area="inventory-item-option-ingredient"]`);
		if (!item) return;

		if (item.classList.contains("main-item")) {
			let next = item.nextElementSibling;
			while (next && !next.classList.contains("main-item") && next.matches(`[data-area="inventory-item-option-ingredient"]`)) {
				const toRemove = next;
				next = next.nextElementSibling;
				toRemove.remove();
			}
			const searchWrapper = item.nextElementSibling;
			if (searchWrapper?.classList.contains("b-recipe-form__search-input-wrapper")) {
				searchWrapper.remove();
			}
		}

		item.remove();
		this.refreshInputNames();
		this.updateInventoryItemsVisibility();
	}

	static openDeleteIngredientDialog(deleteBtn)
	{
		const dialog = document.getElementById(this.deleteIngredientDialogId);
		if (!dialog) return;

		this.pendingIngredientDeleteBtn = deleteBtn;

		const item = deleteBtn.closest(`[data-area="inventory-item-option-ingredient"]`);
		const msgEl = dialog.querySelector("[data-purpose=\"delete-dialog-message\"]");
		if (item && msgEl) {
			const nameEl = item.querySelector(".b-recipe-form__ingredient-name .head, .b-recipe-form__ingredient-name .val");
			const ingredientName = (nameEl && nameEl.textContent.trim()) || "";
			const inventoryEl = item.closest("[data-area=\"inventory\"]");
			const inventoryNameInput = inventoryEl && inventoryEl.querySelector("[data-purpose=\"inventory-name\"]");
			const inventoryTitle = (inventoryNameInput && inventoryNameInput.value.trim()) || "";
			const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
			if (inventoryTitle) {
				msgEl.innerHTML = `Opravdu chcete odstranit surovinu <strong>${esc(ingredientName)}</strong> ze seznamu <strong>${esc(inventoryTitle)}</strong>?`;
			} else {
				msgEl.innerHTML = `Opravdu chcete odstranit surovinu <strong>${esc(ingredientName)}</strong>?`;
			}
		}

		const onClose = () => {
			this.pendingIngredientDeleteBtn = null;
			dialog.removeEventListener("close", onClose);
		};
		dialog.addEventListener("close", onClose);

		this.showModalSafe(dialog);
	}

	// --- Delete Ingredient List ---

	static openDeleteIngredientListDialog(btn)
	{
		const dialog = document.getElementById(this.deleteIngredientListDialogId);
		if (!dialog) return;

		this.pendingIngredientListDeleteBtn = btn;

		const listEl = btn.closest("[data-area=\"inventory\"]");
		const msgEl = dialog.querySelector("[data-purpose=\"delete-dialog-message\"]");
		if (listEl && msgEl) {
			const inventoryNameInput = listEl.querySelector("[data-purpose=\"inventory-name\"]");
			const inventoryTitle = (inventoryNameInput && inventoryNameInput.value.trim()) || "";
			if (inventoryTitle) {
				const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
				msgEl.innerHTML = `Opravdu chcete odstranit seznam surovin <strong>${esc(inventoryTitle)}</strong>?`;
			} else {
				msgEl.textContent = "Opravdu chcete odstranit celý seznam surovin?";
			}
		}

		const onClose = () => {
			this.pendingIngredientListDeleteBtn = null;
			dialog.removeEventListener("close", onClose);
		};
		dialog.addEventListener("close", onClose);

		this.showModalSafe(dialog);
	}

	// --- Delete Step ---

	static openDeleteStepDialog(deleteBtn)
	{
		const step = deleteBtn.closest(`[data-area="step"]`);
		if (!step) return;

		const dialog = document.getElementById(this.deleteStepDialogId);
		if (!dialog) return;

		this.pendingStepDeleteBtn = deleteBtn;

		const onClose = () => {
			this.pendingStepDeleteBtn = null;
			dialog.removeEventListener("close", onClose);
		};
		dialog.addEventListener("close", onClose);

		this.showModalSafe(dialog);
	}

	static openCancelDialog(link)
	{
		const href = link.getAttribute("href");
		if (!href) return;

		const dialog = document.getElementById(this.cancelDialogId);
		if (!dialog) return;

		this.pendingCancelUrl = href;

		const onClose = () => {
			this.pendingCancelUrl = null;
			dialog.removeEventListener("close", onClose);
		};
		dialog.addEventListener("close", onClose);

		this.showModalSafe(dialog);
	}

	static handleDeleteStep(deleteBtn)
	{
		const step = deleteBtn.closest(`[data-area="step"]`);
		if (!step) return;

		step.remove();
		this.refreshInputNames();
		this.updateStepRemoveVisibility();
	}

	// --- Add Step ---

	static handleAddStep()
	{
		const template = document.getElementById("recipe-form-step");
		if (!template) return;

		const stepList = document.querySelector(".b-recipe-form__steps-list");
		if (!stepList) return;

		const step = template.content.cloneNode(true);
		stepList.appendChild(step);

		this.refreshInputNames();
		this.updateStepRecipesVisibility();
		this.updateStepRemoveVisibility();

		const steps = stepList.querySelectorAll(`[data-area="step"]`);
		steps[steps.length - 1]?.querySelector(`[data-purpose="description"]`)?.focus();

		document.querySelectorAll(".drag-drop-upload__files").forEach(el => {
			Sortable?.create?.(el, { draggable: ".drag-drop-upload__file" });
		});
	}

	// --- Ingredient lists ---

	static handleAddIngredientList()
	{
		const template = document.getElementById("recipe-form-ingredient-list");
		if (!template) return;

		const clone = template.content.cloneNode(true);
		const wrap = document.querySelector(".b-recipe-form__ingredient-lists");
		if (wrap) {
			wrap.appendChild(clone);
		}

		this.refreshInputNames();
		this.updateInventoryItemsVisibility();
	}

	static handleDeleteIngredientList(btn)
	{
		const ingredientList = btn.closest(".b-recipe-form__ingredient-list");
		if (!ingredientList) return;
		ingredientList.remove();
		this.refreshInputNames();
	}

	// --- Inventory items visibility ---

	static updateInventoryItemsVisibility()
	{
		const form = this.getForm();
		if (!form) return;

		form.querySelectorAll(".b-recipe-form__inventory-items").forEach(container => {
			const hasItems = container.querySelectorAll(`[data-area="inventory-item-option-ingredient"]`).length > 0;
			container.classList.toggle("b-recipe-form__inventory-items--empty", !hasItems);
		});
	}

	static updateStepRemoveVisibility()
	{
		const form = this.getForm();
		if (!form) return;

		const steps = form.querySelectorAll(`[data-area="step"]`);
		const single = steps.length === 1;
		steps.forEach(step => {
			const btn = step.querySelector(".b-recipe-form__step-remove");
			if (btn) btn.style.visibility = single ? "hidden" : "";
		});
	}

	static updateStepRecipesVisibility()
	{
		const form = this.getForm();
		if (!form) return;

		form.querySelectorAll(".b-recipe-form__step-recipes-list").forEach(list => {
			list.hidden = list.querySelectorAll(".b-recipe-form__step-recipe").length === 0;
		});
	}

	// --- Refresh Input Names ---

	static refreshInputNames()
	{
		const form = this.getForm();
		if (!form) return;

		let inventoryKey = 1;
		form.querySelectorAll(`[data-area="inventory"]`).forEach(inventory => {

			let inventoryItemKey = 0;
			let inventoryItemOptionKey = 1;
			const inventoryItemOptionIngredientKey = 1;
			let productKey = 0;

			const nameInput = inventory.querySelector(`[data-purpose="inventory-name"]`);
			if (nameInput) {
				nameInput.name = `inventories[${inventoryKey}][name]`;
				nameInput.id = `inventory-name-${inventoryKey}`;
				const nameCell = nameInput.closest(".b-input-cell");
				const nameLabel = nameCell?.querySelector("label");
				if (nameLabel) nameLabel.setAttribute("for", nameInput.id);
			}
			const searchInput = inventory.querySelector(".b-recipe-form__ingredient-search");
			if (searchInput) {
				searchInput.id = `inventory-search-${inventoryKey}`;
				const searchCell = searchInput.closest(".b-input-cell");
				const searchLabel = searchCell?.querySelector("label");
				if (searchLabel) searchLabel.setAttribute("for", searchInput.id);
			}

			inventory.querySelectorAll(`[data-area="inventory-item-option-ingredient"]`).forEach(item => {

				if (item.classList.contains("alt-item")) {
					inventoryItemOptionKey++;
				} else if (item.classList.contains("specific-item")) {
					productKey++;
				} else {
					inventoryItemKey++;
					inventoryItemOptionKey = 1;
				}

				const prefix = `inventories[${inventoryKey}][items][${inventoryItemKey}][options][${inventoryItemOptionKey}][ingredients][${inventoryItemOptionIngredientKey}]`;
				const productPrefix = `inventories[${inventoryKey}][items][${inventoryItemKey}][options][${inventoryItemOptionKey}][products][${productKey}]`;

				this.setInputName(item, "ingredient-name-id", `${prefix}[ingredientNameId]`);
				this.setInputName(item, "ingredient-name", `${prefix}[ingredientNameString]`);
				this.setInputName(item, "net-amount", `${prefix}[netAmount]`);
				this.setInputName(item, "practical-unit-id", `${prefix}[practicalUnitId]`);
				this.setInputName(item, "comment", `${prefix}[comment]`);
				this.setInputName(item, "product-source", `${productPrefix}[productSource]`);
				this.setInputName(item, "product-id", `${productPrefix}[productId]`);
			});

			inventoryKey++;
		});

		let stepsKey = 1;
		form.querySelectorAll(".b-recipe-form__steps-list").forEach(stepsArea => {
			stepsArea.querySelectorAll(`[data-area="step"]`).forEach(step => {
				this.setInputName(step, "description", `steps[${stepsKey}][description]`);
				this.setInputIdAndLabel(step, "description", `step-${stepsKey}-description`);

				const dragDrop = step.querySelector(".drag-drop-upload");
				if (dragDrop) {
					const ddForm = new BDragDropUploadForm(dragDrop);
					ddForm.setFileInputName(`steps[${stepsKey}][storageFileIds][]`);
				}

				this.setInputName(step, "videoUrl", `steps[${stepsKey}][videoUrl]`);
				this.setInputIdAndLabel(step, "videoUrl", `step-${stepsKey}-videoUrl`);
				this.setInputIdAndLabel(step, "recipe-search", `step-${stepsKey}-recipe-search`);

				step.querySelectorAll(`[data-purpose="recipe-id"]`).forEach(recipeId => {
					recipeId.name = `steps[${stepsKey}][recipeIds][]`;
				});

				stepsKey++;
			});
		});
	}

	static setInputName(container, purpose, name)
	{
		const el = container.querySelector(`[data-purpose="${purpose}"]`);
		if (el) el.name = name;
	}

	static setInputIdAndLabel(container, purpose, id)
	{
		const el = container.querySelector(`[data-purpose="${purpose}"]`);
		if (!el) return;
		el.id = id;
		const label = el.closest(".b-input-cell")?.querySelector("label");
		if (label) label.setAttribute("for", id);
	}

	static initUnitSelects(container)
	{
		container.querySelectorAll(`select.b-select`).forEach(el => {
			BSelect.initializeElement(el);
		});
	}
}
